import { Injectable, inject } from "@angular/core";
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldAppearance, FloatLabelType } from "@angular/material/form-field";
import { MAT_PAGINATOR_DEFAULT_OPTIONS } from "@angular/material/paginator";
import {
  MAT_SNACK_BAR_DEFAULT_OPTIONS,
  MatSnackBarHorizontalPosition,
  MatSnackBarVerticalPosition
} from "@angular/material/snack-bar";
import { MAT_TOOLTIP_DEFAULT_OPTIONS, TooltipPosition } from "@angular/material/tooltip";
import { UserProfile } from "@app/app-modules/user-profiles/user-profiles-model";
import { SystemSettingsService } from "@app/core/services/system-settings.service";
import { UserSettingsService } from "@app/core/services/user-settings.service";
import { Domains } from "@app/portals/admin-modules/domains/domains-model";
import { ICON_FONT_SET } from "@app/shared/constants/icons.constants";
import {
  ContextMenuSettings,
  HORIZONTAL_POSITIONS,
  LABEL_FLOAT,
  MAT_FORM_APPEARANCE,
  TOOLTIP_POSITIONS,
  VERTICAL_POSITIONS
} from "@app/shared/models/portal-settings.model";

@Injectable({
  providedIn: "root"
})
export class PortalSettingsService {
  private userSettingsService = inject(UserSettingsService);
  private systemService = inject(SystemSettingsService);
  formFieldDefaultOptions = inject(MAT_FORM_FIELD_DEFAULT_OPTIONS);
  tooltipDefaultOptions = inject(MAT_TOOLTIP_DEFAULT_OPTIONS);
  snackBarDefaultOptions = inject(MAT_SNACK_BAR_DEFAULT_OPTIONS);
  paginatorOptions = inject(MAT_PAGINATOR_DEFAULT_OPTIONS);

  /**
   * Set all global configurations based on user or system config.
   */
  setPortalOptions() {
    this._setTooltipOptions();
    this._setSnackbarOptions();
    this._setFormFieldOptions();
  }

  getDebounce(): number {
    let debounce: number =
      this.userSettingsService.inputSettings?.inputDebounce || this.systemService.inputSettings.inputDebounce;
    if (debounce === undefined) {
      debounce = 500;
    }
    return debounce;
  }

  /**
   * Set defaults to new records in user profiles or system settings.
   * @param data
   */
  setDefaults(data: UserProfile | Domains) {
    if (!data!.iconAppearance.value) {
      data!.iconAppearance.value = ICON_FONT_SET.FA_DEFAULT;
    }

    if (!data!.inputAppearance.value) {
      data!.inputAppearance.value = MAT_FORM_APPEARANCE.OUTLINE;
    }

    if (!data!.inputFloatingLabel.value) {
      data!.inputFloatingLabel.value = LABEL_FLOAT.AUTO;
    }

    if (!data!.inputDebounce.value) {
      data!.inputDebounce.value = this.getDebounce();
    }

    if (!data!.notifyDefaultMessage.value) {
      data!.notifyDefaultMessage.value = "Default Message";
    }

    if (!data!.notifyHorizontalPosition.value) {
      data!.notifyHorizontalPosition.value = HORIZONTAL_POSITIONS.RIGHT;
    }

    if (!data!.notifyVerticalPosition.value) {
      data!.notifyVerticalPosition.value = VERTICAL_POSITIONS.BOTTOM;
    }

    if (!data!.notifyDuration.value) {
      data!.notifyDuration.value = 10000;
    }

    if (!data!.toolTipShowDelay.value) {
      data!.toolTipShowDelay.value = 50;
    }

    if (!data!.toolTipHideDelay.value) {
      data!.toolTipHideDelay.value = 50;
    }

    if (!data!.toolTipHideDelayMobile.value) {
      data!.toolTipHideDelayMobile.value = 50;
    }

    if (!data!.toolTipPosition.value) {
      data!.toolTipPosition.value = TOOLTIP_POSITIONS.ABOVE;
    }
  }

  get contextMenuSettings(): ContextMenuSettings {
    return this.userSettingsService.authService.isAuthenticated()
      ? this.userSettingsService.contextMenuSettings
      : this.systemService.contextMenuSettings;
  }

  /**
   * Set form field options based on user or system config
   */
  private _setFormFieldOptions() {
    this.formFieldDefaultOptions.floatLabel = (this.userSettingsService.inputSettings?.inputFloatingLabel ||
      this.systemService.inputSettings.inputFloatingLabel) as FloatLabelType;
    this.formFieldDefaultOptions.hideRequiredMarker = (this.userSettingsService.inputSettings
      ?.inputHideRequiredMarker || this.systemService.inputSettings.inputHideRequiredMarker) as boolean;

    let inputAppearance =
      this.userSettingsService.inputSettings?.inputAppearance || this.systemService.inputSettings.inputAppearance;
    // Make sure we are using a valid form field appearance. (standard, and legacy are deprecated)
    if (![MAT_FORM_APPEARANCE.FILL, MAT_FORM_APPEARANCE.OUTLINE].includes(inputAppearance)) {
      inputAppearance = MAT_FORM_APPEARANCE.OUTLINE;
    }

    this.formFieldDefaultOptions.appearance = inputAppearance as MatFormFieldAppearance;
    this.paginatorOptions.formFieldAppearance = this.formFieldDefaultOptions.appearance;
  }

  /**
   * Set tooltip options based on system config
   */
  private _setTooltipOptions() {
    this.tooltipDefaultOptions.hideDelay = (this.userSettingsService.tooltipSettings?.toolTipHideDelay ||
      this.systemService.tooltipSettings.toolTipHideDelay) as number;
    this.tooltipDefaultOptions.showDelay = (this.userSettingsService.tooltipSettings?.toolTipShowDelay ||
      this.systemService.tooltipSettings.toolTipShowDelay) as number;
    this.tooltipDefaultOptions.position = (this.userSettingsService.tooltipSettings?.toolTipPosition ||
      this.systemService.tooltipSettings.toolTipPosition) as TooltipPosition;
    this.tooltipDefaultOptions.touchendHideDelay = (this.userSettingsService.tooltipSettings?.toolTipHideDelayMobile ||
      this.systemService.tooltipSettings.toolTipHideDelayMobile) as number;
  }

  /**
   * Set snackbar options based on  system config
   */
  private _setSnackbarOptions() {
    this.snackBarDefaultOptions.announcementMessage = (this.userSettingsService.notificationSettings
      ?.notifyDefaultMessage || this.systemService.notificationSettings.notifyDefaultMessage) as string;
    this.snackBarDefaultOptions.duration = (this.userSettingsService.notificationSettings?.notifyDuration ||
      this.systemService.notificationSettings.notifyDuration) as number;
    this.snackBarDefaultOptions.horizontalPosition = (this.userSettingsService.notificationSettings
      ?.notifyHorizontalPosition ||
      this.systemService.notificationSettings.notifyHorizontalPosition) as MatSnackBarHorizontalPosition;
    this.snackBarDefaultOptions.verticalPosition = (this.userSettingsService.notificationSettings
      ?.notifyVerticalPosition ||
      this.systemService.notificationSettings.notifyVerticalPosition) as MatSnackBarVerticalPosition;
  }
}
